import { Ban, Boxes, CheckCircle2, PackageMinus, RotateCcw, ShieldAlert } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { ErrorState } from '../../../../components/ErrorState';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly, formatDateTime, toIso } from '../../../../lib/formatDate';
import { formatMoney } from '../../../../lib/formatMoney';
import { DisposeStockModal } from '../components/DisposeStockModal';
import { StockBatchModal } from '../components/StockBatchModal';
import { stockBatchesApi } from '../stockApi';
import type { StockBatchDetail } from '../stockTypes';
import { BATCH_STATUS, BATCH_STATUS_LABEL } from '../stockTypes';

type TabKey = 'info';

/** A quantity of zero is muted, like every other quantity in the product. */
function Quantity({ value, unit }: { value: number; unit: string }) {
  return (
    <span className={`font-mono tabular-nums ${value === 0 ? 'text-slate-300' : ''}`}>
      {value} {unit}
    </span>
  );
}

/**
 * One lot of quantity-tracked stock.
 *
 * On the **header** layout: a batch reads as one continuous thing rather than a record whose
 * content splits into panels a reader chooses between.
 *
 * **Dispose sits in the Quantity card's cog, beside its Edit.** That is the cog growing a
 * second VERB rather than a second way to edit — the test CLAUDE.md sets is whether a reader
 * would describe the entry as "changing these details", and destroying stock is not that. It
 * keeps its own permission key there, so it hides for a reader who may correct a figure but
 * not write one off.
 */
export function StockBatchProfilePage() {
  const { id } = useParams<{ id: string }>();

  const [batch, setBatch] = useState<StockBatchDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [editing, setEditing] = useState(false);
  const [disposing, setDisposing] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [tab, setTab] = useState<TabKey>('info');

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      setLoading(true);

      stockBatchesApi
        .get(id, signal)
        .then((next) => {
          if (signal?.aborted) return;

          setBatch(next);

          // Cleared on success, or a failed restore keeps its banner over a page that has since
          // re-read the record and disagrees with it.
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;

          setError(caught instanceof ApiError ? caught : new ApiError('Could not load this batch.', 0));
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  /**
   * Puts a deleted lot back.
   *
   * **No confirm tick and no password**: restoring puts something back, and ceremony on an undo
   * only teaches readers to click through it. It owes no clash check either — a batch number
   * carries no unique index, so there is nothing for it to lose a race against.
   */
  async function restore() {
    if (!id) return;

    setRestoring(true);
    setError(null);

    try {
      await stockBatchesApi.activate(id);
      load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('Could not restore this batch.', 0));
    } finally {
      setRestoring(false);
    }
  }

  const breadcrumb: BreadcrumbSegment[] = [
    'Logistics',
    'Inventory',
    { label: 'Stock Batches', to: '/inventory/stock-batches' },
    batch?.batchNumber ?? 'Batch',
  ];

  // Guarded on having NOTHING to show, never on `error` alone: once a record is up it is still
  // the last good answer, and a failed restore goes to the banner rather than blanking the page.
  if (error && !batch) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState message={error.message} correlationId={error.correlationId} onRetry={() => load()} />
      </div>
    );
  }

  if (loading || !batch) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  const edit = {
    key: 'edit-batch',
    label: 'Edit',
    permission: 'stock-batches.update',
    onClick: () => setEditing(true),
  };

  /**
   * The banner, and all five states render.
   *
   * A banner that appears only when something is wrong teaches the reader to skip the strip,
   * and then it is missed on the day it matters. Amber is legal HERE — the no-yellow rule is
   * about an index row, where the brand colour reads as chrome the shell put there.
   */
  function banner() {
    if (error) {
      return { tone: 'red' as const, icon: <Ban className="h-4 w-4" aria-hidden="true" />, message: error.message };
    }

    if (!batch!.isActive) {
      return {
        tone: 'red' as const,
        icon: <Ban className="h-4 w-4" aria-hidden="true" />,
        message:
          'This batch is deleted, so it counts toward nothing. Its ledger is untouched — a movement is never deleted, so the history of this lot survives.',
      };
    }

    if (batch!.isExpired) {
      return {
        tone: 'red' as const,
        icon: <ShieldAlert className="h-4 w-4" aria-hidden="true" />,
        message:
          'This lot is past its expiry date, so it reads as expired wherever it is shown. Nothing wrote that state — it is derived from the date on every read, so correcting the date corrects it.',
      };
    }

    if (batch!.status === BATCH_STATUS.quarantined) {
      return {
        tone: 'amber' as const,
        icon: <ShieldAlert className="h-4 w-4" aria-hidden="true" />,
        message: batch!.quarantineReason
          ? `Quarantined: ${batch!.quarantineReason}`
          : 'This lot is quarantined and is held out of use until somebody releases it.',
      };
    }

    if (batch!.status === BATCH_STATUS.depleted) {
      return {
        tone: 'slate' as const,
        icon: <CheckCircle2 className="h-4 w-4" aria-hidden="true" />,
        message:
          'This lot is empty. It is kept rather than deleted so its lineage survives — the ledger still says what came in and where it went.',
      };
    }

    return {
      tone: 'slate' as const,
      icon: <CheckCircle2 className="h-4 w-4" aria-hidden="true" />,
      message: `This lot is available to issue. Its ${batch!.quantityOnHand} ${batch!.unitCode} count toward on-hand stock for ${batch!.sku}.`,
    };
  }

  const state = banner();

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="stock-batch-profile"
        header={
          <ProfileHeader
            id="stock-batch-profile-header"
            icon={<Boxes className="h-6 w-6" aria-hidden="true" />}
            title={batch.batchNumber}
            meta={[batch.sku, batch.itemName, batch.storageFacilityName]}
            secondary={[
              `${batch.quantityOnHand} of ${batch.quantityReceived} ${batch.unitCode} left`,
              batch.expiryDate === null ? 'No expiry' : `Expires ${formatDateOnly(batch.expiryDate)}`,
              BATCH_STATUS_LABEL[batch.status],
            ]}
            status={
              <StatusText tone={batch.isActive ? 'active' : 'inactive'}>
                {batch.isActive ? 'Active' : 'Deleted'}
              </StatusText>
            }
            createdBy={batch.createdByName}
            createdAt={batch.createdAt}
          />
        }
        banner={
          <ProfileBanner
            id="stock-batch-profile-banner"
            tone={state.tone}
            icon={state.icon}
            message={state.message}
            action={
              !batch.isActive && !error ? (
                // Gates the button, not the banner: a read-only viewer still needs the state.
                <PermissionGate permission="stock-batches.delete">
                  <Button
                    id="stock-batch-profile-restore"
                    variant="secondary"
                    leading={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => void restore()}
                    disabled={restoring}
                    loading={restoring}
                    loadingLabel="Restoring"
                  >
                    Restore
                  </Button>
                </PermissionGate>
              ) : undefined
            }
          />
        }
        tabs={[{ key: 'info', label: 'Info' }]}
        activeTab={tab}
        onTabChange={setTab}
      >
        <ProfileCard id="stock-batch-lot" title="Lot" menuItems={[edit]}>
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Batch number', value: batch.batchNumber, mono: true },
              { label: 'Item', value: `${batch.sku} — ${batch.itemName}` },
              { label: 'Facility', value: batch.storageFacilityName },
              {
                label: 'Expires',
                // A CALENDAR date — `formatDateOnly`, never the instant path, or a reader west
                // of UTC is told the lot lapsed the day before it did.
                value: batch.expiryDate === null ? null : formatDateOnly(batch.expiryDate),
                mono: true,
              },
              {
                label: 'Condition',
                value: (
                  <StatusText
                    tone={
                      batch.status === BATCH_STATUS.active
                        ? 'active'
                        : batch.status === BATCH_STATUS.depleted
                          ? 'inactive'
                          : 'blocked'
                    }
                  >
                    {BATCH_STATUS_LABEL[batch.status]}
                  </StatusText>
                ),
              },
              { label: 'Quarantine reason', value: batch.quarantineReason },
              { label: 'Notes', value: batch.notes },
            ]}
          />
        </ProfileCard>

        <ProfileCard
          id="stock-batch-quantity"
          title="Quantity"
          menuItems={[
            edit,
            {
              /*
                A second VERB on the cog, not a second Edit. Its own key, so it hides for a
                reader who may correct a figure but not write stock off — and disabled for STATE
                on an empty lot, which says why rather than vanishing.
              */
              key: 'dispose-batch',
              label: 'Dispose',
              icon: <PackageMinus className="h-4 w-4" aria-hidden="true" />,
              permission: 'stock-batches.dispose',
              disabledReason:
                batch.quantityOnHand > 0 ? null : 'This lot holds nothing left to dispose of.',
              onClick: () => setDisposing(true),
            },
          ]}
        >
          <PreviewTable
            variant="table-display"
            rows={[
              {
                label: 'On hand',
                value: <Quantity value={batch.quantityOnHand} unit={batch.unitCode} />,
              },
              {
                label: 'Received',
                value: <Quantity value={batch.quantityReceived} unit={batch.unitCode} />,
              },
              {
                label: 'Consumed',
                value: (
                  <Quantity
                    value={batch.quantityReceived - batch.quantityOnHand}
                    unit={batch.unitCode}
                  />
                ),
              },
              {
                label: 'Unit cost',
                // An absent amount is an em dash, never a zero — an unpriced lot and a free one
                // are different facts.
                value: batch.unitCost === null ? null : formatMoney(batch.unitCost),
                mono: true,
              },
              {
                label: 'Lot value',
                value:
                  batch.unitCost === null
                    ? null
                    : formatMoney(batch.unitCost * batch.quantityOnHand),
                mono: true,
              },
            ]}
          />
        </ProfileCard>

        {/* Audit is the one card that never takes an Edit, at any permission level: it records
            what happened rather than holding a value somebody chose. */}
        <ProfileCard id="stock-batch-audit" title="Audit">
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Created by', value: batch.createdByName },
              {
                label: 'Created',
                value: <time dateTime={toIso(batch.createdAt)}>{formatDateTime(batch.createdAt)}</time>,
                mono: true,
              },
              { label: 'Updated by', value: batch.updatedByName },
              {
                label: 'Updated',
                value:
                  batch.updatedAt === null ? null : (
                    <time dateTime={toIso(batch.updatedAt)}>{formatDateTime(batch.updatedAt)}</time>
                  ),
                mono: true,
              },
            ]}
          />
        </ProfileCard>
      </ProfileHeaderShell>

      {editing ? (
        <StockBatchModal
          open
          editing={batch}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      ) : null}

      {disposing ? (
        <DisposeStockModal
          open
          batch={batch}
          onClose={() => setDisposing(false)}
          onSaved={() => {
            setDisposing(false);
            load();
          }}
        />
      ) : null}
    </div>
  );
}
